import { Link } from "react-router-dom";
import { Download, Smartphone, ShieldCheck, CheckCircle2, Apple } from "lucide-react";
import { API } from "@/lib/api";

// Routed through the API so installs are counted on the Super Admin dashboard;
// it 307-redirects to the static file below.
const APK = `${API}/public/app`;
const APK_DIRECT = `${process.env.PUBLIC_URL}/app/edupulse.apk`;
const VERSION = "1.2";
const SIZE = "2.7 MB";

const STEPS = [
  ["Download the APK", "Tap the button above. The file saves to your Downloads folder."],
  ["Open the file", "Tap the downloaded edupulse.apk. Android may ask permission to install from this source — allow it."],
  ['Tap "Install anyway"', 'Google Play Protect shows "Unable to find information for this app" for every app installed outside the Play Store. It is not a virus warning — tap Install anyway / More details → Install.'],
  ["Open EduPulse", "The app opens the school portal full-screen, no browser bar. Log in with your usual credentials."],
];

export default function GetApp() {
  return (
    <div className="pub-mesh min-h-[85vh]">
      <div className="mx-auto max-w-4xl px-5 py-14" data-testid="get-app-page">
        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600">Mobile App</p>
        <h1 className="mt-1 text-3xl font-bold font-head sm:text-4xl">Get the EduPulse app</h1>
        <p className="mt-3 max-w-xl text-slate-600">
          Install EduPulse on your Android phone for one-tap access to admissions, fees,
          attendance and report cards.
        </p>

        <div className="pub-card mt-8 grid gap-8 p-6 sm:p-8 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="flex items-center gap-4">
              <img
                src={`${process.env.PUBLIC_URL}/android-chrome-192x192.png`}
                alt="EduPulse"
                className="h-16 w-16 rounded-2xl ring-1 ring-slate-200"
              />
              <div>
                <p className="text-lg font-semibold font-head">EduPulse for Android</p>
                <p className="text-sm text-slate-500">
                  Version {VERSION} · {SIZE} · Android 5.0 and up
                </p>
              </div>
            </div>

            <a
              href={APK}
              className="pub-btn mt-6 w-full justify-center sm:w-auto"
              data-testid="download-apk-btn"
            >
              <Download size={17} /> Download APK
            </a>

            <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
              <ShieldCheck size={14} className="mt-0.5 shrink-0 text-emerald-600" />
              Signed build. The app only opens this website — it asks for no extra permissions
              beyond notifications. <a href={APK_DIRECT} className="text-indigo-600 hover:underline">Direct link</a>.
            </p>
          </div>

          <div className="mx-auto text-center">
            <img
              src={`${process.env.PUBLIC_URL}/app/qr.png`}
              alt="Scan to open the download page"
              className="h-40 w-40 rounded-xl ring-1 ring-slate-200"
              data-testid="get-app-qr"
            />
            <p className="mt-2 text-xs text-slate-500">Scan on your phone</p>
          </div>
        </div>

        <h2 className="mt-12 text-xl font-semibold font-head">How to install</h2>
        <ol className="mt-5 space-y-4 border-l-2 border-slate-200 pl-6">
          {STEPS.map(([title, body], i) => (
            <li key={title} className="relative">
              <span className="absolute -left-[33px] flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                {i + 1}
              </span>
              <p className="font-medium">{title}</p>
              <p className="mt-0.5 text-sm text-slate-600">{body}</p>
            </li>
          ))}
        </ol>

        <div className="pub-card mt-10 flex items-start gap-3 p-5">
          <Apple size={20} className="mt-0.5 shrink-0 text-slate-700" />
          <div>
            <p className="font-medium">On an iPhone or iPad?</p>
            <p className="mt-0.5 text-sm text-slate-600">
              Open <span className="font-medium">edupulse.aatreya.org</span> in Safari, tap the
              Share button, then <span className="font-medium">Add to Home Screen</span>. It runs
              full-screen just like an app.
            </p>
          </div>
        </div>

        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500">
          <CheckCircle2 size={15} className="text-emerald-600" />
          Already installed? <Link to="/login" className="font-medium text-indigo-600 hover:underline">Go to login</Link>
        </p>
      </div>
    </div>
  );
}
